import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useBrandManager } from "@/hooks/useBrandManager";
import { usePopupContext } from "@/contexts/PopupContext";
import { useLocation } from "react-router-dom";
import { Bug, Lightbulb, Loader2, AlertCircle } from "lucide-react";

interface RoadmapFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const RoadmapForm = ({ open, onOpenChange }: RoadmapFormProps) => {
  const [type, setType] = useState<'bug' | 'feature'>('bug');
  const [priority, setPriority] = useState<number>(3);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { currentBrand } = useBrandManager();
  const { currentPopup } = usePopupContext();
  const location = useLocation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentBrand) {
      toast({
        title: "Error",
        description: "No brand selected",
        variant: "destructive",
      });
      return;
    }

    if (!description.trim()) {
      toast({
        title: "Error",
        description: "Please provide a description",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Error", 
          description: "You must be logged in to submit feedback",
          variant: "destructive",
        });
        return;
      }
      
      const currentUrl = window.location.href;
      
      const { error } = await supabase
        .from('os_roadmap')
        .insert({
          type,
          description: description.trim(),
          url: currentUrl,
          brand_id: currentBrand.id,
          user_id: user.id,
          priority,
          popup_name: currentPopup?.name || null,
          popup_context: currentPopup?.context || null,
          status: 'open'
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: `${type === 'bug' ? 'Bug report' : 'Feature request'} submitted successfully!`,
      });

      // Reset form
      setDescription('');
      setType('bug');
      setPriority(3);
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting roadmap item:', error);
      toast({
        title: "Error",
        description: "Failed to submit item. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {type === 'bug' ? (
              <>
                <Bug className="h-5 w-5 text-red-500" />
                Report a Bug
              </>
            ) : (
              <>
                <Lightbulb className="h-5 w-5 text-yellow-500" />
                Request a Feature
              </>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="type">Type</Label>
            <Select value={type} onValueChange={(value: 'bug' | 'feature') => setType(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bug">
                  <div className="flex items-center gap-2">
                    <Bug className="h-4 w-4 text-red-500" />
                    Bug Report
                  </div>
                </SelectItem>
                <SelectItem value="feature">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="h-4 w-4 text-yellow-500" />
                    Feature Request
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="priority">Priority</Label>
            <Select value={priority.toString()} onValueChange={(value) => setPriority(Number(value))}>
              <SelectTrigger>
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    1 - Critical
                  </div>
                </SelectItem>
                <SelectItem value="2">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-orange-500" />
                    2 - High
                  </div>
                </SelectItem>
                <SelectItem value="3">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-yellow-500" />
                    3 - Medium
                  </div>
                </SelectItem>
                <SelectItem value="4">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-blue-500" />
                    4 - Low
                  </div>
                </SelectItem>
                <SelectItem value="5">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-gray-500" />
                    5 - Lowest
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="url">Current Page</Label>
            <Input 
              id="url"
              value={location.pathname}
              disabled
              className="bg-muted"
            />
          </div>

          {currentPopup && (
            <div className="space-y-2">
              <Label htmlFor="popup-context">Current Popup</Label>
              <Input 
                id="popup-context"
                value={`${currentPopup.name} - ${currentPopup.context}`}
                disabled
                className="bg-muted"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                type === 'bug' 
                  ? "Describe what went wrong and how to reproduce it..."
                  : "Describe the feature you'd like to see..."
              }
              className="min-h-[100px]"
              required
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};